import { ChangeDetectionStrategy, Component, input, OnChanges, output, SimpleChange } from '@angular/core';

const PASSWORD_STRENGTH_COLORS = ['darkred', 'orangered', 'orange', 'yellowgreen', 'green'];
const PASSWORD_MIN_LENGTH = 12;

@Component({
  selector: 'pp-password-strength',
  standalone: true,
  templateUrl: './password-strength.component.html',
  styleUrls: ['./password-strength.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordStrengthComponent implements OnChanges {
  bars = ['', '', '', '', ''];
  readonly passwordToCheck = input('');
  readonly isFormValid = input(false);

  readonly passwordStrength = output<boolean>();

  ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
    const password = changes['passwordToCheck'].currentValue;

    this.#updatePasswordStrength(password);
  }

  /**
   * Evaluates the strength of the given password.
   * @param password - The password to evaluate.
   * @returns The strength level of the password.
   */
  #checkStrength(password: string) {
    let passwordLevel = 0;

    const regex = /[$-/:-?{-~!"^_@#`[\]]/g;
    const lowerLetters = /[a-z]+/.test(password);
    const upperLetters = /[A-Z]+/.test(password);
    const numbers = /[0-9]+/.test(password);
    const symbols = regex.test(password);
    const passwordLength = password.length >= PASSWORD_MIN_LENGTH;
    const flags = [lowerLetters, upperLetters, numbers, symbols, passwordLength];
    const passedMatches = flags.filter(Boolean).length;

    passwordLevel += passedMatches * 10;

    const LevelForceMap: { [key: number]: number } = { 0: 0, 1: 10, 2: 20, 3: 30, 4: 40, 5: 50 };
    passwordLevel = LevelForceMap[passedMatches];

    return passwordLevel;
  }

  /**
   * Determines the color and index based on the password strength.
   * @param strength - The strength level of the password.
   * @returns An object containing the index and color.
   */
  #getColor(strength: number) {
    let index = 0;

    switch (strength) {
      case 10: {
        index = 0;

        break;
      }
      case 20: {
        index = 1;

        break;
      }
      case 30: {
        index = 2;

        break;
      }
      case 40: {
        index = 3;

        break;
      }
      default: {
        if (strength >= 50) {
          index = this.isFormValid() ? 4 : 3;
        }
      }
    }

    return {
      index: index + 1,
      color: PASSWORD_STRENGTH_COLORS[index],
    };
  }

  /**
   * Sets the color of the bars up to the specified count.
   * @param count - The number of bars to color.
   * @param color - The color to apply to the bars.
   */
  #setBarColors(count: number, color: string): void {
    for (let n = 0; n < count; n++) {
      this.bars[n] = color;
    }
  }

  /**
   * Updates the visual representation of the password strength based on the evaluated strength.
   * @param password - The password to evaluate.
   */
  #updatePasswordStrength(password: string): void {
    this.#setBarColors(5, 'var(--color-bg-3)');

    if (password) {
      const pwdStrength = this.#checkStrength(password);
      this.passwordStrength.emit(pwdStrength >= 50);

      const color = this.#getColor(pwdStrength);
      this.#setBarColors(color.index, color.color);
    }
  }
}
